import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { PhoneNumberUtil } from 'google-libphonenumber';
import { PermissionsService } from '../../services/permissions/permissions.service';

@Component({
  selector: 'app-mobile-number-modal',
  templateUrl: './mobile-number-modal.component.pug',
  styleUrls: ['./mobile-number-modal.component.scss'],
  standalone: false
})
export class MobileNumberModalComponent implements OnInit {
  @Input() phone: string = '';
  @Input() isVerified: boolean = false;
  public event: EventEmitter<any> = new EventEmitter();

  mobile: string = '';
  initialMobile: string = '';
  isLoading: boolean = false;
  isPhoneValid: boolean = true;
  showValidationError: boolean = false;
  ent_10313_password_policy_and_mfa: boolean = false;

  private phoneUtil = PhoneNumberUtil.getInstance();

  get hasChanges(): boolean {
    return this.mobile !== this.initialMobile;
  }

  /**
   * Determines if the save button should be disabled.
   * - Disabled when no changes and (value is empty/country-code-only OR number is verified and unchanged)
   *   so we don't send a redundant API call when the user opens with empty and does nothing.
   * - Otherwise enabled (user can verify, or save a change including clearing the number).
   */
  get isSaveDisabled(): boolean {
    if (this.hasChanges) {
      return false;
    }
    return (
      this.isEmptyOrCountryCodeOnly(this.mobile) ||
      this.isVerified
    );
  }

  constructor(
    public _bsModalRef: BsModalRef,
    private permissions: PermissionsService,
  ) {}

  ngOnInit(): void {
    this.ent_10313_password_policy_and_mfa = this.permissions.isFeatureActivated('ent-10313-password-policy-and-mfa');
    this.mobile = this.phone || '';
    this.initialMobile = this.mobile;
    if (this.mobile) {
      this.validatePhoneNumber();
    }
  }

  onPhoneUpdate(update: any) {
    this.mobile = update?.phone || '';
    this.showValidationError = false;
    this.validatePhoneNumber();
  }

  /**
   * Returns true if the value is effectively "no number" (empty or country code only).
   */
  private isEmptyOrCountryCodeOnly(value: string): boolean {
    const trimmed = (value || '').trim();
    if (!trimmed) {
      return true;
    }
    if (/^null$/i.test(trimmed)) {
      return true;
    }
    // Pre-parse: avoid calling parse() on country-code-only (e.g. "+61", "92")
    if (/^\+\d{1,3}$/.test(trimmed) || /^\d{1,3}$/.test(trimmed)) {
      return true;
    }
    try {
      const phoneToValidate = trimmed.startsWith('+') ? trimmed : '+' + trimmed;
      const parsed = this.phoneUtil.parse(phoneToValidate);
      const national = parsed.getNationalNumber();
      return national == null || Number(national) === 0;
    } catch {
      // Treat as country-code-only if value looks like +digits (1–4 digits)
      return /^\s*\+?\s*\d{1,4}\s*$/.test(trimmed);
    }
  }

  /**
   * Validates the phone number using google-libphonenumber.
   * Empty or country-code-only is treated as valid.
   */
  private validatePhoneNumber(): void {
    if (this.isEmptyOrCountryCodeOnly(this.mobile)) {
      this.isPhoneValid = true;
      return;
    }
    try {
      const phoneToValidate = this.mobile.startsWith('+') ? this.mobile : '+' + this.mobile;
      const parsedPhone = this.phoneUtil.parse(phoneToValidate);
      this.isPhoneValid = this.phoneUtil.isValidNumber(parsedPhone);
    } catch (e) {
      this.isPhoneValid = false;
    }
  }

  cancel() {
    this._bsModalRef.hide();
  }

  save() {
    // Validate and show error if invalid
    this.validatePhoneNumber();
    if (!this.isPhoneValid) {
      this.showValidationError = true;
      return;
    }

    // For verified numbers with no changes, just close
    if (this.isVerified && !this.hasChanges) {
      this._bsModalRef.hide();
      return;
    }

    // Emit the phone number. For unverified numbers (even without changes),
    // this triggers the verification flow. For changed numbers, this triggers
    // the verification modal. Parent will call closeModal() after verification
    // succeeds or if no verification is needed.
    this.event.emit({ phone: this.mobile, action: 'save' });
  }

  /**
   * Called by parent after verification flow completes to close this modal.
   */
  closeModal() {
    this.initialMobile = this.mobile;
    this._bsModalRef.hide();
  }

  /**
   * Resets the mobile value to initial state (used when verification is cancelled).
   */
  resetToInitial() {
    this.mobile = this.initialMobile;
    this.showValidationError = false;
    if (this.mobile) {
      this.validatePhoneNumber();
    } else {
      this.isPhoneValid = true;
    }
  }
}


